import { useState } from 'react';
import { Tag as TagIcon, X, Eye, EyeOff, Users } from 'lucide-react';
import { useTicketCollab } from '../../hooks/useTicketCollab';
import { useAuth } from '../../hooks/useAuth';
import Avatar from '../ui/Avatar';

export default function CollabPanel({ ticketId, initialTags }: { ticketId: string; initialTags?: string[] }) {
  const { user } = useAuth();
  const { tags, allTags, watchers, saveTags, watch, unwatch } = useTicketCollab(ticketId, initialTags ?? []);
  const [draft, setDraft] = useState('');

  const addTag = (value: string) => {
    const v = value.trim();
    if (v && !tags.includes(v)) saveTags([...tags, v]);
    setDraft('');
  };
  const removeTag = (t: string) => saveTags(tags.filter(x => x !== t));

  const amWatching = watchers.some(w => w.user.id === user?.id);

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 space-y-4">
      {/* Tags */}
      <div>
        <h4 className="flex items-center gap-1.5 text-sm font-semibold text-navy mb-2"><TagIcon size={14} /> Tags</h4>
        <div className="flex flex-wrap items-center gap-1.5">
          {tags.map(t => (
            <span key={t} className="inline-flex items-center gap-1 text-xs font-medium bg-brand/10 text-brand-dark rounded-full pl-2.5 pr-1.5 py-1">
              {t}
              <button onClick={() => removeTag(t)} className="hover:text-red-500" aria-label={`Remove ${t}`}><X size={12} /></button>
            </span>
          ))}
          <input
            list="all-tags"
            value={draft}
            onChange={e => setDraft(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTag(draft); } }}
            onBlur={() => draft && addTag(draft)}
            placeholder="Add tag…"
            className="text-xs border border-gray-200 rounded-full px-3 py-1 w-28 focus:outline-none focus:ring-2 focus:ring-brand/30"
          />
          <datalist id="all-tags">{allTags.map(t => <option key={t} value={t} />)}</datalist>
        </div>
      </div>

      {/* Watchers */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h4 className="flex items-center gap-1.5 text-sm font-semibold text-navy"><Users size={14} /> Watchers {watchers.length > 0 && <span className="text-gray-400">({watchers.length})</span>}</h4>
          <button
            onClick={() => amWatching ? unwatch(user!.id) : watch()}
            className="inline-flex items-center gap-1 text-xs font-medium text-brand hover:underline"
          >
            {amWatching ? <><EyeOff size={13} /> Unwatch</> : <><Eye size={13} /> Watch</>}
          </button>
        </div>
        {watchers.length === 0 ? (
          <p className="text-xs text-gray-400">No watchers. Watchers are notified of updates.</p>
        ) : (
          <ul className="space-y-1.5">
            {watchers.map(w => (
              <li key={w.id} className="flex items-center gap-2 group">
                <Avatar name={w.user.name} size="sm" />
                <span className="text-sm text-navy flex-1 truncate">{w.user.name}</span>
                <button onClick={() => unwatch(w.user.id)} className="text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100" aria-label="Remove watcher"><X size={14} /></button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
